import React from 'react';
import { Tooltip } from 'reactstrap';
import PropTypes from 'prop-types';

const phoneNumberPattern = /^(?!(\d)\(\1{3}\))(?!(\d)\2{9})(?!(\d)\3{2}.\3{3}.\3{4})(?:\(^[2-9]\d{2}\)|^[2-9]\d{2})(.?)?\d{3}\4\d{4}$/;

const propTypes = {    
    autoFocus: PropTypes.bool,       // Focus on input on load   
    disabled: PropTypes.bool,        // Disable input
    id: PropTypes.string,            // Input id    
    inputErrorMsg: PropTypes.string, // Input inline error message   
    name: PropTypes.string,          // Input name 
    onChange: PropTypes.func,            // Function triggered on onChange
    placeholder: PropTypes.string,   // Input placeholder text
    required: PropTypes.bool,        // Set to true so input empty validation check will trigger on blur and keypress
    setFocus: PropTypes.bool,        // True sets the focus on input
    showAsInvalid: PropTypes.bool,   // Override component validation and display input as invalid
    showClearBtn: PropTypes.bool,    // Display clear input icon button    
    tooltipID: PropTypes.string,     // Tooltip id which is required to display tooltip message
    tooltipMsg: PropTypes.string,    // Tooltip message    
    value:  PropTypes.string // Input value
};

export default class PhoneNumber extends React.Component {
    constructor(props) {
        super(props);
        this.toggleTooltip = this.toggleTooltip.bind(this);
        this.validateOnBlur = this.validateOnBlur.bind(this);
        this.clearInput = this.clearInput.bind(this);
        this.validateOnMount = this.validateOnMount.bind(this);
        const { value } = this.props;
        this.state = {
            tooltipOpen: false,
            invalidInput: false,
            isPhoneNumberEmpty: value ? false : true,
            touched: false,
            enableClearBtn: false
        };

        this.genericInputEl = React.createRef();
        this.parentOfInput = React.createRef();
    }

    componentDidMount() {
        this._isMounted = true;
        const { setFocus, validateOnMount } = this.props;
        if (setFocus) {
            const input = this.genericInputEl.current;    
            if (input) {
                input.focus();
            }
        }
        if (validateOnMount) {
            this.validateOnMount();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }


    clearInput(event) {
        if (this._isMounted) {
            const input = this.genericInputEl.current;
            input.value = "";
            var e = { event, target: { value: "", name: this.props.name ? this.props.name : "" } };
            this.onChange(e);
            input.focus();
        }
    }

    setClearBtn(value) {
        setTimeout(() => { this._isMounted && this.setState({ enableClearBtn: value }) }, 300);
    }


    toggleTooltip() {
        this.setState({
            tooltipOpen: !this.state.tooltipOpen
        });
    }

    isNumericInput = (event) => {
        const key = event.keyCode;
        return ((key >= 48 && key <= 57) || // Allow number line
            (key >= 96 && key <= 105) // Allow number pad
        );
    };
    
    isModifierKey = (event) => {
        const key = event.keyCode;
        return (event.shiftKey === true || key === 35 || key === 36) || // Allow Shift, Home, End
            (key === 8 || key === 9 || key === 13 || key === 46) || // Allow Backspace, Tab, Enter, Delete
            (key > 36 && key < 41) || // Allow left, up, right, down
            (
                (event.ctrlKey === true || event.metaKey === true) &&
                (key === 65 || key === 67 || key === 86 || key === 88 || key === 90)
            )
    };

    onKeyPress = (event) => {

        if(!this.isNumericInput(event) && !this.isModifierKey(event)){
            event.preventDefault();
        }
    };

    onKeyUp = (e) => {      
        if (this.isModifierKey(e)) { return; }       
        var formatTarget = e.target;
        const input = e.target.value.replace(/\D/g, '').substring(0, 10); // First ten digits of input only
        const first = input.substring(0, 3);
        const middle = input.substring(3, 6);
        const last = input.substring(6, 10);
        if (input.length > 5) {
            formatTarget.value = `${first}.${middle}.${last}`;
        } else if (input.length > 2) {
            formatTarget.value = `${first}.${middle}`;
        } else if (input.length > 0) {
            this.setState({ invalidInput: false });
            formatTarget.value = `${first}`;
        }
        
    }

    onChange(event) {
        const { value } = event.target;
        const isValid = this.isValid(value);

        this.setState({
            isPhoneNumberEmpty: !value.trim()
        });

        if (this.props.onChange) {
            this.props.onChange(event, isValid);
        }
    }

    isValid(value) {
        if (value) {
            return value.trim() && phoneNumberPattern.test(value);
        }
        else {
            return this.props.required ? false : true;
        }
    }

    validateOnBlur(event) {
        const isValid = this.isValid(event.target.value);

        if (this.props.onChange) {
            this.props.onChange(event, isValid);
        }

        event.persist();
        this.setState({ invalidInput: !isValid, touched: true });
    }

    validateOnMount() {
        const value = this.props.value;
        const isValid = this.isValid(value);
        this.setState({ invalidInput: !isValid });
    }

    onParentOfInputBlur = e => {
        const { showClearBtn } = this.props;
        if (showClearBtn) { this.setClearBtn(false) }
    }
    
    render() {
        const { placeholder, value, required, name, id, autoFocus, disabled,
            inputErrorMsg, tooltipID, tooltipMsg, showAsInvalid, showClearBtn
        } = this.props;
        const { isPhoneNumberEmpty } = this.state;
        const isValid = (!required && isPhoneNumberEmpty) || this.isValid(value);
        const containerClassName = (value ? " input-not-empty" : " input-empty") +
                                    (this.state.touched ? " input-touched" : " input-untouched") +
                                    (this.state.invalidInput || showAsInvalid ? " input-invalid" : " input-valid") +
                                    (showAsInvalid ? " input-show-invalid" : "") +
                                    ((tooltipID && tooltipMsg) ? " input-append" : "");

        return (
            <div className={"il-group" + containerClassName} ref={this.parentOfInput} onBlur={e => this.onParentOfInputBlur(e)}>
                <input type="text"  
                    className="form-control"
                    value={ value }
                    required = { required }
                    name={name}
                    id={id || name}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    placeholder={placeholder ? placeholder : "Phone Number"} maxLength={12}
                    onKeyUp={e => this.onKeyUp(e)} onKeyDown={e => this.onKeyPress(e)}
                    onChange={e => this.onChange(e)} 
                    onBlur={this.validateOnBlur}
                    onFocus={e => this.setClearBtn(true)}
                    //aria-label={placeholder ? placeholder : 'Phone Number'}
                    title={placeholder ? "Please fill out the " + placeholder : "Please fill out the phone number."}
                    ref={this.genericInputEl}
                />

                {showClearBtn && value && this.state.enableClearBtn &&
                    <div className="clear-btn-container">
                        <button className="clear-btn" type="button" aria-label="clear button" onClick={this.clearInput}><span className="icon-input-clear" /></button>
                    </div>
                }

                {(this.state.invalidInput || showAsInvalid) &&
                    <div className="input-icon-append input-icon__error">
                        <span className="icon-input-error" />
                    </div>
                }

                {(tooltipID && tooltipMsg) && <div className="input-icon-append">
                    <span className="icon-info" id={tooltipID} />
                    <Tooltip placement="top" isOpen={this.state.tooltipOpen} autohide={false} target={tooltipID} toggle={this.toggleTooltip}>
                        {tooltipMsg}
                    </Tooltip>
                </div>}
                <div className="il-label" aria-hidden="true">{placeholder}</div>
                {this.state.invalidInput || showAsInvalid ? <div className="input-error-msg" htmlFor={id || name} tabIndex="0">{inputErrorMsg ? inputErrorMsg : <>Enter a valid <span className="input-error-msg__placeholder">{placeholder}</span>.</>}</div> : null}
            </div>    
        );
    }
}

PhoneNumber.propTypes = propTypes;